import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Container, Box, Paper, Typography, List, ListItemButton, ListItemText,
    Button, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useTheme, useMediaQuery
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import NavigationBar from '../components/NavigationBar';
import MermaidDiagram from '../components/MermaidDiagram';

// Section definitions
const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'getting-started', title: 'Getting Started' },
    { id: 'data-requirements', title: 'Data Requirements' },
    { id: 'distribution-identification', title: 'Distribution Identification' },
    { id: 'capability-metrics', title: 'Capability Metrics' },
    { id: 'non-normal-data', title: 'Non-Normal Data' },
    { id: 'interpreting-results', title: 'Interpreting Results' },
    { id: 'control-charts', title: 'Control Charts' },
    { id: 'limitations', title: 'Limitations' },
    { id: 'statistical-references', title: 'Statistical References' },
    { id: 'statistical-definitions', title: 'Statistical Definitions' },
    { id: 'validation', title: 'Validation Status (Beta)' },
    { id: 'data-privacy', title: 'Data Privacy' },
];

function DocumentationPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [activeSection, setActiveSection] = useState('overview');
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const location = useLocation();

    const NAV_OFFSET = 80; // matches scrollMarginTop on Section components

    const scrollToSection = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
        window.scrollTo({ top, behavior: 'smooth' });
        setActiveSection(id);
    };

    // Handle hash navigation — use rAF+timeout double-flush to ensure DOM is ready
    useEffect(() => {
        if (!location.hash) return;
        const id = location.hash.slice(1);
        let cancelled = false;
        requestAnimationFrame(() => {
            const t = setTimeout(() => {
                if (!cancelled) scrollToSection(id);
            }, 50);
            return () => clearTimeout(t);
        });
        return () => { cancelled = true; };
    }, [location.hash]); // eslint-disable-line react-hooks/exhaustive-deps

    // Track scroll position to update active section in sidebar
    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + NAV_OFFSET + 8;
            for (const section of sections) {
                const element = document.getElementById(section.id);
                if (element) {
                    const { offsetTop, offsetHeight } = element;
                    if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
                        setActiveSection(section.id);
                        break;
                    }
                }
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNavClick = (sectionId) => {
        scrollToSection(sectionId);
        setMobileNavOpen(false);
    };

    const Navigation = () => (
        <List component="nav" dense>
            {sections.map((section) => (
                <ListItemButton
                    key={section.id}
                    selected={activeSection === section.id}
                    onClick={() => handleNavClick(section.id)}
                    sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                            backgroundColor: 'primary.dark',
                            '&:hover': { backgroundColor: 'primary.dark' }
                        }
                    }}
                >
                    <ListItemText primary={section.title} />
                </ListItemButton>
            ))}
        </List>
    );

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <NavigationBar />
        <Box sx={{ display: 'flex' }}>
            {/* Desktop Navigation Sidebar */}
            {!isMobile && (
                <Paper
                    sx={{
                        width: 240,
                        flexShrink: 0,
                        position: 'fixed',
                        top: 48,
                        left: 0,
                        height: 'calc(100vh - 48px)',
                        overflowY: 'auto',
                        borderRadius: 0,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        p: 2
                    }}
                >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        Documentation
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Navigation />
                </Paper>
            )}

            {/* Mobile Navigation Header */}
            {isMobile && (
                <>
                    <Paper
                        sx={{
                            position: 'fixed',
                            top: 48,
                            left: 0,
                            right: 0,
                            zIndex: 1100,
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            borderRadius: 0
                        }}
                    >
                        <Button
                            onClick={() => setMobileNavOpen(!mobileNavOpen)}
                            startIcon={mobileNavOpen ? <CloseIcon /> : <MenuIcon />}
                        >
                            {mobileNavOpen ? 'Close' : 'Menu'}
                        </Button>
                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                            Documentation
                        </Typography>
                    </Paper>
                    {mobileNavOpen && (
                        <Paper
                            sx={{
                                position: 'fixed',
                                top: 56,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1099,
                                p: 2,
                                overflowY: 'auto',
                                borderRadius: 0
                            }}
                        >
                            <Navigation />
                        </Paper>
                    )}
                </>
            )}

            {/* Main Content */}
            <Container
                maxWidth="md"
                sx={{
                    ml: isMobile ? 0 : '240px',
                    pt: isMobile ? 8 : 4,
                    pb: 4,
                    px: { xs: 2, sm: 3 }
                }}
            >
                {/* Overview */}
                <Section id="overview" title="Overview">
                    <Typography paragraph>
                        Capability Cockpit is a web-based application for statistical analysis of process
                        capability. It helps you understand whether your manufacturing or measurement process
                        is stable and capable of meeting specifications.
                    </Typography>
                    <Typography paragraph>
                        <strong>What it does:</strong>
                    </Typography>
                    <ul>
                        <li>Calculates process capability indices (Cp, Cpk, Pp, Ppk)</li>
                        <li>Identifies the best-fitting statistical distribution for your data</li>
                        <li>Generates control charts to detect process instability</li>
                        <li>Estimates defect rates (PPM) based on your data distribution</li>
                        <li>Provides capability equivalents for non-normal data</li>
                    </ul>
                    <Typography paragraph>
                        <strong>What it is not:</strong>
                    </Typography>
                    <ul>
                        <li>Not a replacement for engineering judgment</li>
                        <li>Not suitable for real-time process monitoring</li>
                        <li>Not a comprehensive SPC suite (no CUSUM, EWMA, or multivariate charts)</li>
                    </ul>
                </Section>

                {/* Getting Started */}
                <Section id="getting-started" title="Getting Started">
                    <Typography variant="h6" gutterBottom>Basic Workflow</Typography>
                    <ol>
                        <li><strong>Enter dataset description</strong> - A name or identifier for your analysis</li>
                        <li><strong>Enter specification limits</strong> - USL (Upper Spec Limit) and LSL (Lower Spec Limit)</li>
                        <li><strong>Enter measurement data</strong> - Paste from Excel or type values in the data grid</li>
                        <li><strong>Click Submit</strong> - The analysis runs automatically</li>
                        <li><strong>Review results</strong> - Check metrics, control charts, and any warnings</li>
                    </ol>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Data Entry Tips</Typography>
                    <ul>
                        <li>Copy/paste from Excel is supported (Ctrl+V)</li>
                        <li>For individual measurements, enter one value per row in the second column</li>
                        <li>For subgroup data, enable "Subgroup Mode" and enter subgroup ID in the first column</li>
                        <li>Leave either USL or LSL blank for one-sided specifications</li>
                    </ul>
                </Section>

                {/* Data Requirements */}
                <Section id="data-requirements" title="Data Requirements">
                    <Typography variant="h6" gutterBottom>Specification Limits</Typography>
                    <Typography paragraph>
                        Enter at least one specification limit (USL or LSL). For two-sided capability analysis,
                        both limits are required. The application will calculate different metrics depending on
                        what limits are provided.
                    </Typography>

                    <Typography variant="h6" gutterBottom>Sample Size Guidance</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Sample Size</TableCell>
                                    <TableCell>Reliability</TableCell>
                                    <TableCell>Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>&lt; 25</TableCell>
                                    <TableCell>Low</TableCell>
                                    <TableCell>Distribution tests have very low power; results may be misleading</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>25 - 50</TableCell>
                                    <TableCell>Moderate</TableCell>
                                    <TableCell>Acceptable for initial assessment; interpret with caution</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>50 - 100</TableCell>
                                    <TableCell>Good</TableCell>
                                    <TableCell>Reasonable confidence in distribution identification</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>&gt; 100</TableCell>
                                    <TableCell>High</TableCell>
                                    <TableCell>Strong statistical power; reliable capability estimates</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" gutterBottom>Subgroup Data</Typography>
                    <Typography paragraph>
                        When you have natural subgroups (e.g., samples taken from the same batch, shift, or machine),
                        enable Subgroup Mode. Enter a subgroup identifier in the first column and the measurement
                        value in the second column. The application will calculate X-bar/R charts and within-subgroup
                        variation for short-term capability (Cp/Cpk).
                    </Typography>
                </Section>

                {/* Distribution Identification */}
                <Section id="distribution-identification" title="Distribution Identification">
                    <Typography paragraph>
                        Real-world process data often does not follow a normal distribution. The application fits
                        multiple distributions to your data and helps you identify the best fit.
                    </Typography>

                    <Typography variant="h6" gutterBottom>Supported Distributions</Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Distribution</TableCell>
                                    <TableCell>Parameters</TableCell>
                                    <TableCell>Use Case</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>Normal</TableCell>
                                    <TableCell>mean, std dev</TableCell>
                                    <TableCell>Symmetric, bell-shaped data</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Lognormal (2P/3P)</TableCell>
                                    <TableCell>mu, sigma, [threshold]</TableCell>
                                    <TableCell>Right-skewed data with natural lower bound</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Weibull (2P/3P)</TableCell>
                                    <TableCell>shape, scale, [threshold]</TableCell>
                                    <TableCell>Life data, reliability analysis, flexible skewness</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Gamma (2P/3P)</TableCell>
                                    <TableCell>shape, scale, [threshold]</TableCell>
                                    <TableCell>Waiting times, right-skewed positive data</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Exponential (1P/2P)</TableCell>
                                    <TableCell>rate, [threshold]</TableCell>
                                    <TableCell>Time between events, highly skewed</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Johnson SU</TableCell>
                                    <TableCell>gamma, delta, xi, lambda</TableCell>
                                    <TableCell>Flexible 4-parameter for complex distributions</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" gutterBottom>Auto Mode (Recommended)</Typography>
                    <Typography paragraph>
                        Auto Mode automatically selects a distribution using conservative statistical criteria aligned
                        with standard SPC practice:
                    </Typography>
                    <ul>
                        <li>Only considers distributions with p-value &ge; 0.05 (acceptable fit)</li>
                        <li>Prefers simpler models (2-parameter over 3-parameter) unless LRT shows significant improvement</li>
                        <li><strong>Prefers Normal when adequate:</strong> If Normal is not rejected (p &ge; 0.05), Normal is
                            recommended regardless of whether other distributions have better AIC scores</li>
                        <li>Uses AIC to select among non-normal distributions only when Normal is rejected</li>
                        <li>Displays selection rationale in a recommendation banner</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Distribution Selection Logic (Auto Mode)</Typography>
                    <Typography paragraph>
                        The following diagram illustrates the decision logic used by Auto Mode to select a distribution:
                    </Typography>
                    <MermaidDiagram
                        chart={`flowchart TD
    A[Fit 10 candidate distributions via MLE] --> B[Compute AD statistic and p-value for each]
    B --> C{p-value >= 0.05?}
    C -->|Yes| D[Mark as ACCEPTABLE]
    C -->|No| E[Mark as REJECTED]
    D --> F[Group acceptable fits by distribution family]
    F --> G{Family has both 2P and 3P variants?}
    G -->|Yes| H[Compute LRT]
    G -->|No| I[Use available variant]
    H --> J{LRT p-value < 0.05 AND 3P stable?}
    J -->|Yes| K[Prefer 3P for this family]
    J -->|No| L[Prefer 2P for this family]
    K --> M[Collect preferred variant from each family]
    L --> M
    I --> M
    M --> N{Normal acceptable?}
    N -->|Yes| O[Recommend Normal - SPC convention]
    N -->|No| P{Multiple families remain?}
    P -->|No| Q[Recommend single distribution]
    P -->|Yes| R[Rank by AIC]
    R --> S{AIC difference > 2?}
    S -->|Yes| T[Recommend lowest AIC]
    S -->|No| U[Report multiple acceptable fits]
    E --> V{Any acceptable fits?}
    V -->|Yes| F
    V -->|No| W[FALLBACK: Select highest p-value among rejected]`}
                    />
                    <Typography paragraph variant="body2" color="text.secondary">
                        <strong>Key behaviors:</strong> P-values are used as a gating criterion (pass/fail at α = 0.05), not as a ranking metric.
                        <strong>Normal preference:</strong> Following conservative SPC convention, if Normal passes the goodness-of-fit
                        gate, it is recommended even if other distributions have better AIC scores. This reflects the principle that
                        the initial assumption of normality should not be abandoned unless statistically rejected. AIC ranking is used
                        only when Normal is rejected. Simpler 2-parameter models are preferred over 3-parameter variants unless the
                        likelihood ratio test provides statistical justification. When no distributions meet the acceptance criterion,
                        fallback behavior selects the least-bad fit (highest p-value among rejected) with appropriate warnings.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Goodness-of-Fit Metrics</Typography>
                    <Typography paragraph variant="body2" color="text.secondary">
                        Distribution parameters are estimated using maximum likelihood estimation (MLE) (Casella & Berger).
                    </Typography>
                    <ul>
                        <li><strong>AD (Anderson-Darling):</strong> Test statistic measuring fit quality (Stephens, 1974). Lower is better.</li>
                        <li><strong>P-value:</strong> Statistical significance. P &ge; 0.05 means acceptable fit.</li>
                        <li><strong>AIC:</strong> Akaike Information Criterion (Akaike, 1974). Lower is better, penalizes complexity.</li>
                        <li><strong>LRT:</strong> Likelihood Ratio Test comparing 3P vs 2P models (Casella & Berger). P &le; 0.05 favors 3P.</li>
                    </ul>
                </Section>

                {/* Capability Metrics */}
                <Section id="capability-metrics" title="Capability Metrics">
                    <Typography variant="h6" gutterBottom>For Normal Data</Typography>
                    <Typography paragraph variant="body2" color="text.secondary">
                        Capability indices follow standard definitions (Montgomery).
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Metric</TableCell>
                                    <TableCell>Formula Basis</TableCell>
                                    <TableCell>Interpretation</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell><strong>Cp</strong></TableCell>
                                    <TableCell>(USL - LSL) / 6*sigma_within</TableCell>
                                    <TableCell>Short-term capability (potential)</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell><strong>Cpk</strong></TableCell>
                                    <TableCell>min(Cpu, Cpl) using sigma_within</TableCell>
                                    <TableCell>Short-term capability accounting for centering</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell><strong>Pp</strong></TableCell>
                                    <TableCell>(USL - LSL) / 6*sigma_overall</TableCell>
                                    <TableCell>Long-term performance (actual)</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell><strong>Ppk</strong></TableCell>
                                    <TableCell>min(Ppu, Ppl) using sigma_overall</TableCell>
                                    <TableCell>Long-term performance accounting for centering</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Typography variant="h6" gutterBottom>Interpreting Capability Values</Typography>
                    <ul>
                        <li><strong>Cpk &lt; 1.0:</strong> Process is not capable. High defect rate expected.</li>
                        <li><strong>Cpk 1.0 - 1.33:</strong> Marginally capable. Monitor closely.</li>
                        <li><strong>Cpk 1.33 - 1.67:</strong> Capable. Industry standard for most applications.</li>
                        <li><strong>Cpk &gt; 1.67:</strong> Highly capable. Required for safety-critical applications.</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>PPM (Defects Per Million)</Typography>
                    <Typography paragraph>
                        PPM estimates the expected defect rate based on the process distribution relative to specification limits.
                    </Typography>
                    <ul>
                        <li><strong>PPM (Within):</strong> Based on short-term sigma (potential defect rate)</li>
                        <li><strong>PPM (Overall):</strong> Based on overall sigma (actual expected defect rate)</li>
                    </ul>
                </Section>

                {/* Non-Normal Data */}
                <Section id="non-normal-data" title="Non-Normal Data Handling">
                    <Typography paragraph>
                        When data does not follow a normal distribution (p-value &lt; 0.05 for Normal), traditional
                        Cp/Cpk indices are misleading. The application calculates <strong>capability equivalents</strong>
                        using the percentile method.
                    </Typography>

                    <Typography variant="h6" gutterBottom>Percentile Method</Typography>
                    <Typography paragraph>
                        Instead of using &plusmn;3 sigma to define the process spread, the percentile method uses the
                        0.135th and 99.865th percentiles of the fitted distribution (Clements, 1989). These percentiles correspond to
                        &plusmn;3 sigma coverage for a normal distribution, making the results comparable.
                    </Typography>

                    <Typography variant="h6" gutterBottom>Equivalent Metrics</Typography>
                    <ul>
                        <li><strong>Pp (equiv):</strong> Based on distribution spread between 0.135th and 99.865th percentiles</li>
                        <li><strong>Ppk (equiv):</strong> Based on distance from median to nearest spec limit using percentiles</li>
                        <li><strong>PPM (equiv):</strong> Defect rate from the fitted distribution's CDF at spec limits</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>When to Use Equivalents</Typography>
                    <Typography paragraph>
                        Use equivalent metrics when:
                    </Typography>
                    <ul>
                        <li>Normal distribution has p-value &lt; 0.05</li>
                        <li>Another distribution has significantly better fit</li>
                        <li>Histogram shows clear skewness or non-bell shape</li>
                        <li>Probability plot shows departure from the reference line</li>
                    </ul>
                </Section>

                {/* Interpreting Results */}
                <Section id="interpreting-results" title="Interpreting Results">
                    <Typography variant="h6" gutterBottom>Workflow for Analysis</Typography>
                    <ol>
                        <li><strong>Check control charts first.</strong> If the process is unstable (points outside limits),
                            capability indices are not meaningful.</li>
                        <li><strong>Check normality.</strong> Look at the Normal p-value. If &lt; 0.05, consider alternative distributions.</li>
                        <li><strong>Select appropriate distribution.</strong> Use Auto Mode or manually select based on fit metrics.</li>
                        <li><strong>Review capability metrics.</strong> Use Cp/Cpk for normal data, equivalents for non-normal.</li>
                        <li><strong>Check warnings.</strong> Review any alerts about stability, fit issues, or edge cases.</li>
                    </ol>

                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Understanding the Probability Plot</Typography>
                    <Typography paragraph>
                        The probability plot shows your data against the theoretical distribution. Points falling
                        on or near the reference line indicate good fit. Systematic departures (S-curves, bends)
                        indicate poor fit.
                    </Typography>

                    <Typography variant="h6" gutterBottom>Shape Diagnostics</Typography>
                    <ul>
                        <li><strong>Skewness:</strong> Measures asymmetry. Positive = right tail, Negative = left tail. Near 0 = symmetric (normal = 0).</li>
                        <li><strong>Kurtosis:</strong> Excess kurtosis measuring tail heaviness. &gt; 0 = heavy tails, &lt; 0 = light tails (normal = 0).</li>
                        <li><strong>Bowley Skewness:</strong> Robust skewness using quartiles. Less sensitive to outliers.</li>
                        <li><strong>Tail Ratio:</strong> Compares tail spread. Normal distribution &#8776; 2.91.</li>
                    </ul>
                </Section>

                {/* Control Charts */}
                <Section id="control-charts" title="Control Charts">
                    <Typography paragraph>
                        Control charts help detect special-cause variation (process instability). The application
                        generates Shewhart-style control charts appropriate for your data type.
                    </Typography>

                    <Typography variant="h6" gutterBottom>Individual Measurements (I-MR)</Typography>
                    <ul>
                        <li><strong>I-Chart (Individuals):</strong> Plots each measurement with control limits based on
                            moving range. Detects shifts in process mean.</li>
                        <li><strong>MR-Chart (Moving Range):</strong> Plots difference between consecutive measurements.
                            Detects changes in short-term variation.</li>
                    </ul>

                    <Typography variant="h6" gutterBottom>Subgroup Data (X-bar/R)</Typography>
                    <ul>
                        <li><strong>X-bar Chart:</strong> Plots subgroup means with control limits. More sensitive than I-chart
                            for detecting mean shifts.</li>
                        <li><strong>R-Chart:</strong> Plots subgroup ranges. Detects changes in within-subgroup variation.</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Reading Control Charts</Typography>
                    <ul>
                        <li><strong>UCL/LCL:</strong> Upper/Lower Control Limits (typically &plusmn;3 sigma from center)</li>
                        <li><strong>Points outside limits:</strong> Indicate likely special-cause variation</li>
                        <li><strong>Patterns:</strong> Runs, trends, or cycles may indicate assignable causes</li>
                    </ul>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Note: Shewhart charts are robust for detecting large shifts but may not detect small,
                        gradual changes. For high-sensitivity monitoring, consider additional methods (not included
                        in this application).
                    </Typography>
                </Section>

                {/* Limitations */}
                <Section id="limitations" title="Limitations">
                    <Typography variant="h6" gutterBottom>Statistical Limitations</Typography>
                    <ul>
                        <li><strong>Small samples:</strong> With fewer than 25-30 observations, distribution tests have low power
                            and may fail to reject incorrect distributions.</li>
                        <li><strong>Large samples:</strong> With very large samples (&gt;500), even minor departures from normality
                            become statistically significant, even if practically unimportant.</li>
                        <li><strong>3-parameter models:</strong> Threshold parameters can be numerically unstable when the threshold
                            is close to the data minimum. Check for stability warnings.</li>
                        <li><strong>Multiple testing:</strong> Fitting many distributions increases the chance of finding a
                            "good fit" by chance. Use Auto Mode's conservative selection logic.</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Application Limitations</Typography>
                    <ul>
                        <li>No multivariate capability analysis</li>
                        <li>No CUSUM or EWMA charts for small-shift detection</li>
                        <li>No automated outlier removal (by design - user should investigate outliers)</li>
                        <li>Maximum recommended dataset size: ~10,000 observations for responsive performance</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>When Results May Be Misleading</Typography>
                    <ul>
                        <li>Process is unstable (special causes present)</li>
                        <li>Data contains multiple populations or modes</li>
                        <li>Measurements are truncated or censored</li>
                        <li>Specification limits are unrealistic or incorrectly entered</li>
                    </ul>
                </Section>

                {/* Statistical Foundations and References */}
                <Section id="statistical-references" title="Statistical Foundations and References">
                    <Typography paragraph>
                        The statistical methods implemented in this application are based on established literature
                        in quality engineering and statistical inference.
                    </Typography>

                    <Typography variant="h6" gutterBottom>Process Capability Analysis</Typography>
                    <ul>
                        <li>
                            Montgomery, D.C. <em>Introduction to Statistical Quality Control</em>. Wiley.{' '}
                            <a href="https://www.wiley.com/en-us/Introduction+to+Statistical+Quality+Control-p-9781119723089"
                               target="_blank" rel="noopener noreferrer">Publisher Link</a>
                        </li>
                        <li>
                            Clements, J.A. (1989). Process Capability Calculations for Non-Normal Distributions.
                            <em> Quality Progress</em>.{' '}
                            <a href="https://asq.org/quality-resources/articles/process-capability-calculations-for-non-normal-distributions?id=4f0b3e7bdbd4475c9a2bfc3f3db59d41"
                               target="_blank" rel="noopener noreferrer">ASQ Resource</a>
                        </li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Goodness-of-Fit Testing</Typography>
                    <ul>
                        <li>
                            Stephens, M.A. (1974). EDF Statistics for Goodness of Fit and Some Comparisons.
                            <em> Journal of the American Statistical Association</em>, 69(347), 730–737.{' '}
                            <a href="https://www.jstor.org/stable/2286009"
                               target="_blank" rel="noopener noreferrer">JSTOR</a>
                        </li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Statistical Inference and Model Selection</Typography>
                    <ul>
                        <li>
                            Casella, G., and Berger, R.L. <em>Statistical Inference</em>. Cengage Learning.{' '}
                            <a href="https://www.cengage.com/c/statistical-inference-2e-casella/"
                               target="_blank" rel="noopener noreferrer">Publisher Link</a>
                        </li>
                        <li>
                            Akaike, H. (1974). A New Look at the Statistical Model Identification.
                            <em> IEEE Transactions on Automatic Control</em>, 19(6), 716–723.{' '}
                            <a href="https://ieeexplore.ieee.org/document/1100705"
                               target="_blank" rel="noopener noreferrer">IEEE Xplore</a>
                        </li>
                        <li>
                            Schwarz, G. (1978). Estimating the Dimension of a Model.
                            <em> Annals of Statistics</em>, 6(2), 461–464.{' '}
                            <a href="https://projecteuclid.org/euclid.aos/1176344136"
                               target="_blank" rel="noopener noreferrer">Project Euclid</a>
                        </li>
                    </ul>
                </Section>

                {/* Statistical Definitions and Industry Comparisons */}
                <Section id="statistical-definitions" title="Statistical Definitions and Industry Comparisons">
                    <Typography paragraph>
                        This section documents specific statistical definition choices that may cause small numeric
                        differences when comparing results to other software tools.
                    </Typography>

                    <Typography variant="h6" gutterBottom>Skewness</Typography>
                    <Typography paragraph>
                        The application reports <strong>Fisher-adjusted sample skewness</strong> (also known as G1),
                        computed from sample moments with a small-sample bias correction. For a normal distribution,
                        skewness equals zero. Positive values indicate right-skewness (longer right tail); negative
                        values indicate left-skewness.
                    </Typography>
                    <Typography paragraph variant="body2" color="text.secondary">
                        Some software tools compute moment-based skewness without bias correction, or apply different
                        adjustment factors. These formula variations can produce small numeric differences across tools,
                        particularly with smaller sample sizes. Such differences are expected and do not indicate errors.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Kurtosis</Typography>
                    <Typography paragraph>
                        The application reports <strong>excess kurtosis</strong> (also known as G2), which subtracts 3
                        from the raw kurtosis value. For a normal distribution, excess kurtosis equals zero. Positive
                        values indicate heavier tails than normal; negative values indicate lighter tails.
                    </Typography>
                    <Typography paragraph variant="body2" color="text.secondary">
                        Raw kurtosis (sometimes called "kurtosis" without qualification) equals excess kurtosis plus 3.
                        A normal distribution has raw kurtosis of 3 but excess kurtosis of 0. Some tools report raw
                        kurtosis without clear labeling, which can cause apparent discrepancies of exactly 3.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Rationale</Typography>
                    <Typography paragraph>
                        The excess kurtosis convention (normal = 0) provides intuitive interpretation: values near zero
                        suggest normal-like tails, while deviations from zero indicate departure from normality. This
                        convention is consistent with standard statistical literature and enables direct comparison
                        across different distribution types.
                    </Typography>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Impact on Analysis</Typography>
                    <Typography paragraph>
                        Skewness and kurtosis are provided for <strong>descriptive and diagnostic context only</strong>.
                        These shape statistics do not affect:
                    </Typography>
                    <ul>
                        <li>Goodness-of-fit tests (Anderson-Darling p-values)</li>
                        <li>Distribution selection logic (Auto Mode decisions)</li>
                        <li>Model comparison metrics (AIC, LRT)</li>
                        <li>PPM defect rate calculations</li>
                        <li>Capability indices (Cp, Cpk, Pp, Ppk, or equivalents)</li>
                        <li>Control chart limits</li>
                    </ul>
                    <Typography paragraph variant="body2" color="text.secondary">
                        Differences in skewness or kurtosis values between this application and other tools reflect
                        formula conventions only and have no impact on the analytical conclusions.
                    </Typography>
                </Section>

                {/* Validation Status (Beta) */}
                <Section id="validation" title="Validation Status (Beta)">
                    <ul>
                        <li>
                            <strong>Core Descriptive Statistics — Validated</strong>
                            <Typography variant="body2" component="p" sx={{ mt: 0.5, mb: 1.5 }}>
                                Mean, standard deviation, variance, skewness, and kurtosis have been benchmarked
                                against industry-standard tools. Minor differences may occur due to rounding or
                                bias-correction conventions.
                            </Typography>
                        </li>
                        <li>
                            <strong>Distribution Identification — Validated</strong>
                            <Typography variant="body2" component="p" sx={{ mt: 0.5, mb: 1.5 }}>
                                Candidate distributions are evaluated using likelihood-based criteria (AIC/BIC)
                                with goodness-of-fit tests as supporting evidence. This approach meets or exceeds
                                common benchmarking methods that rely solely on GOF p-values.
                            </Typography>
                        </li>
                        <li>
                            <strong>Non-Normal Capability Metrics — In Progress</strong>
                            <Typography variant="body2" component="p" sx={{ mt: 0.5 }}>
                                Non-normal capability is computed using distribution-based tail probabilities.
                                Results are statistically defensible but may differ slightly from legacy tools
                                due to methodological differences. Additional cross-tool benchmarking is ongoing.
                            </Typography>
                        </li>
                    </ul>
                </Section>

                {/* Data Privacy */}
                <Section id="data-privacy" title="Data Privacy">
                    <Typography variant="h6" gutterBottom>How Your Data Is Handled</Typography>
                    <Typography paragraph>
                        To enable the History feature, the application stores an anonymous session identifier
                        in your browser (HTTP-only cookie). This identifier links your submissions to your browser
                        session without collecting personal information.
                    </Typography>

                    <Typography variant="h6" gutterBottom>What Is Stored</Typography>
                    <ul>
                        <li>Your submitted measurement data and specification limits</li>
                        <li>Calculated results (capability indices, fit parameters)</li>
                        <li>Anonymous session identifier</li>
                    </ul>

                    <Typography variant="h6" gutterBottom>What Is NOT Collected</Typography>
                    <ul>
                        <li>Personal identifying information</li>
                        <li>IP addresses (beyond standard web server logs)</li>
                        <li>Third-party tracking or analytics</li>
                    </ul>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Data Retention</Typography>
                    <Typography paragraph>
                        Submissions are stored in the database indefinitely. Clearing your browser cookies
                        will prevent you from accessing your previous submissions (the data remains but is
                        no longer linked to your browser).
                    </Typography>

                    <Typography variant="h6" gutterBottom>Disclaimer</Typography>
                    <Typography paragraph>
                        Capability Cockpit provides statistical calculations and visualizations for informational
                        and decision-support purposes only. Results depend on input data accuracy and appropriateness.
                        No warranties are made regarding fitness for any particular purpose. Do not use results
                        as the sole basis for regulatory, quality, or manufacturing decisions without appropriate
                        professional judgment and validation.
                    </Typography>
                </Section>

                {/* Footer */}
                <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                        Capability Cockpit Documentation | Last updated: January 2026
                    </Typography>
                </Box>
            </Container>
        </Box>
        </Box>
    );
}

// Reusable Section component
function Section({ id, title, children }) {
    return (
        <Paper
            id={id}
            sx={{
                p: 3,
                mb: 4,
                scrollMarginTop: '80px'
            }}
        >
            <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                {title}
            </Typography>
            {children}
        </Paper>
    );
}

export default DocumentationPage;
